import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SettingsContent from "./SettingsContent";
import GenericModal from "./GenericModal";
import InviteContent from "./InviteContent";
import GetStartedContent from "./GetStartedContent";
import NotificationsContent from "./NotificationContent";
import LinearProgressBar from "../../../Components/LinearProgressBar";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useLocation } from "react-router-dom";
import ProfileDrawer from "../../../Components/MyProfile";
import { soonBadge } from "../styles/styles";
import { useTranslation } from "react-i18next";
import { KEILOT_WHATSAPP_NUMBER } from "../../../Constants/config";
import { useSocketContext } from "../../../Context/SocketContext/SocketProvider";
import { useWeeklyTasks } from "../../../Containers/GrowCommunity/hooks/useGrowCommunity";
import emitter from "../../../Utils/events/eventBus";

const BottomNavMenu = ({
  isBelowMd,
  handleItemClick,
  activeScreen,
  setActiveScreen,
  isCommunityManagementRole,
  isMoMView,
  sidebarData,
  closeDrawer,
  languages = [],
  onLanguageChange,
  getStartedContent,
  fetchGetStartedContent,
  setTosUpdateDialog,
}) => {
  const location = useLocation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [activeModalText, setActiveModalText] = useState("");
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileDrawerTab, setProfileDrawerTab] = useState("profile");
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);

  const { t, i18n } = useTranslation(["bottom-menu-and-settings", "tos-modal"]);
  const isHebrew = i18n.language === "he";

  const bottomMenuItems = [
    // No community-level invite in MoM view — the MoM has no single community.
    isCommunityManagementRole && !isMoMView && {
      text: "Invite",
      iconPath: "/icons/sidebar-icons/user-plus.svg",
    },
    {
      text: "Notifications",
      iconPath: "/icons/sidebar-icons/notifications.svg",
    },
    {
      text: "Settings",
      iconPath: "/icons/sidebar-icons/settings.svg",
    },
    {
      text: "Help",
      iconPath: "/icons/sidebar-icons/help.svg",
    },
  ].filter(Boolean);

  const handleModalClick = async (eventTargeted, text) => {
    const rect = eventTargeted.getBoundingClientRect();
    setModalPosition({ top: rect.bottom, left: rect.left });
    setActiveModalText(text);
    setIsModalVisible(true);
  };

  const { supplierTasks, getStartedTasks } = getStartedContent || {};

  const { percentageComplete: supplierPercentageComplete } =
    supplierTasks || {};

  const { percentageComplete: adminPercentageComplete } = getStartedTasks || {};

  // FE-first preview (Grow your community): once setup is complete the Get
  // Started entry becomes "this week's tasks". Real users never get the flag.
  const { t: tg } = useTranslation("page-grow-community");
  const weekly = useWeeklyTasks();
  const showWeekly =
    isCommunityManagementRole &&
    getStartedContent?.weeklyTasksPreview &&
    adminPercentageComplete >= 100;

  const { unreadNotificationsCount } = useSocketContext();

  const handleCloseGenericModal = useCallback(() => {
    setIsModalVisible(false);
    setActiveModalText("");
  }, []);

  useEffect(() => {
    handleCloseGenericModal();
  }, [location.pathname, handleCloseGenericModal]);

  const handleNotificationDrawerClose = () => {
    setNotificationDrawerOpen(false);
  };

  return (
    <>
      <Box
        alignItems={"flex-start"}
        justifyContent={"flex-start"}
        flexDirection={"column"}
      >
        {isBelowMd && activeScreen && (
          <Box
            onClick={() => setActiveScreen("")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: "32px",
              mt: "32px",
              marginLeft: "-4px",
              cursor: "pointer",
            }}
          >
            {isHebrew ? (
              <KeyboardArrowRightIcon />
            ) : (
              <KeyboardArrowLeftOutlinedIcon />
            )}
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: "500",
                ml: "8px",
              }}
            >
              {t("back")}
            </Typography>
          </Box>
        )}
        {!isMoMView && (activeScreen === "get-started" || !activeScreen) && (
          <Box
            sx={{
              border:
                activeScreen !== "get-started"
                  ? "1px solid rgba(234, 234, 234, 1)"
                  : "none",
              borderRadius: "12px",
              padding: "14px 12px",
              mb: !isBelowMd ? "0px" : "24px",
              cursor: !isBelowMd ? "pointer" : "auto",
            }}
            onClick={async (e) => {
              const eventTargeted = e.currentTarget;
              await fetchGetStartedContent();
              isBelowMd
                ? handleItemClick("get-started")
                : handleModalClick(eventTargeted, "get-started");
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {showWeekly ? tg("tasks.sidebar_title") : t("get_started")}
              </Typography>
              {activeScreen !== "get-started" &&
                (isHebrew ? <ChevronLeftIcon /> : <ChevronRightOutlinedIcon />)}
            </Box>
            <Box
              sx={{
                mt: "14px",
              }}
            >
              <LinearProgressBar
                percentage={
                  isCommunityManagementRole
                    ? showWeekly
                      ? weekly.percentage
                      : adminPercentageComplete
                    : supplierPercentageComplete
                }
                showPercentageText={true}
                wrapperStyle={{
                  gap: "8px",
                }}
              />
            </Box>
          </Box>
        )}
        {isBelowMd && (
          <Box>
            {activeScreen === "get-started" && (
              <GetStartedContent
                isCommunityManagementRole={isCommunityManagementRole}
                modalPosition={modalPosition}
                isBelowMd={isBelowMd}
                getStartedContent={getStartedContent}
                closeDrawer={closeDrawer}
              />
            )}

            {activeScreen === "invite" && (
              <InviteContent
                modalPosition={modalPosition}
                isBelowMd={isBelowMd}
                sidebarData={sidebarData}
              />
            )}
            {activeScreen === "settings" && (
              <SettingsContent
                modalPosition={modalPosition}
                setIsProfileOpen={setIsProfileOpen}
                setProfileDrawerTab={setProfileDrawerTab}
                isBelowMd={isBelowMd}
                sidebarData={sidebarData}
                languages={languages}
                onLanguageChange={onLanguageChange}
              />
            )}

            <NotificationsContent
              isBelowMd={isBelowMd}
              notificationDrawerOpen={notificationDrawerOpen}
              handleNotificationDrawerClose={handleNotificationDrawerClose}
            />
          </Box>
        )}
        {!activeScreen && (
          <Box sx={{ mt: "22px" }}>
            {bottomMenuItems?.map((item, idx) => {
              const { iconPath, text, disabled } = item || {};
              return (
                <ListItem
                  key={idx + text}
                  sx={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    py: "10px",
                    px: "8px",
                    cursor: "pointer",
                    pointerEvents: disabled ? "none" : "auto",
                    opacity: disabled ? 0.5 : 1,
                  }}
                  onClick={(e) => {
                    if (
                      isBelowMd &&
                      item?.text?.toLowerCase() === "notifications"
                    ) {
                      setNotificationDrawerOpen(true);
                    } else if (item?.text?.toLowerCase() === "help") {
                      window.open(
                        `https://api.whatsapp.com/send/?phone=${KEILOT_WHATSAPP_NUMBER}&text=${t(
                          "help_whatsapp_message_text",
                          { ns: "bottom-menu-and-settings" },
                        )} &app_absent=0`,
                        "_blank",
                        "noopener,noreferrer",
                      );
                      closeDrawer();
                    } else {
                      isBelowMd
                        ? handleItemClick(item?.text?.toLowerCase())
                        : handleModalClick(
                            e.currentTarget,
                            item?.text?.toLowerCase(),
                          );
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", position: "relative" }}>
                    <img src={iconPath} alt="logo" className="menu-item-logo" />
                    {text === "Notifications" &&
                      unreadNotificationsCount > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "-7px",
                            minWidth: "15px",
                            height: "15px",
                            px: "3px",
                            borderRadius: "8px",
                            backgroundColor: "rgba(255, 76, 81, 1)",
                            border: "1.5px solid rgba(255, 255, 255, 1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "9px",
                              fontWeight: 600,
                              lineHeight: 1,
                              color: "#fff",
                            }}
                          >
                            {unreadNotificationsCount}
                          </Typography>
                        </Box>
                      )}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(text.toLowerCase())}
                    color="rgba(62, 63, 76, 1)"
                    sx={{
                      fontSize: "15px",
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                      flex: "unset",
                      m: "0px",
                    }}
                  />

                  {disabled && <Box sx={soonBadge}>{t("soon")}</Box>}
                </ListItem>
              );
            })}
            {isModalVisible && (
              <GenericModal
                open={isModalVisible}
                onClose={handleCloseGenericModal}
                modalPosition={modalPosition}
              >
                <Box>
                  {activeModalText === "get-started" && (
                    <GetStartedContent
                      isCommunityManagementRole={isCommunityManagementRole}
                      modalPosition={modalPosition}
                      isBelowMd={isBelowMd}
                      getStartedContent={getStartedContent}
                      handleCloseGenericModal={handleCloseGenericModal}
                    />
                  )}
                  {activeModalText === "invite" && (
                    <InviteContent
                      modalPosition={modalPosition}
                      sidebarData={sidebarData}
                    />
                  )}
                  {activeModalText === "notifications" && (
                    <NotificationsContent
                      modalPosition={modalPosition}
                      isBelowMd={isBelowMd}
                      handleNotificationDrawerClose={handleCloseGenericModal}
                    />
                  )}
                  {activeModalText === "settings" && (
                    <SettingsContent
                      modalPosition={modalPosition}
                      setIsProfileOpen={setIsProfileOpen}
                      setProfileDrawerTab={setProfileDrawerTab}
                      setIsModalVisible={setIsModalVisible}
                      sidebarData={sidebarData}
                      languages={languages}
                      onLanguageChange={onLanguageChange}
                    />
                  )}
                </Box>
              </GenericModal>
            )}
          </Box>
        )}
        {!activeScreen && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
              paddingLeft: "12px",
              paddingRight: "4px",
              py: "8px",
              mb: "16px",
            }}
          >
            <Typography
              color="rgba(62, 63, 76, 0.7)"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
              }}
            >
              © 2025, Keilot |
            </Typography>
            <Typography
              color="rgba(62, 63, 76, 0.7)"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                textDecoration: "underline",
                cursor: "pointer",
              }}
              // Keilot's own terms now live in one window, opened read-only
              // from here; the community's own TOS keeps its own dialog.
              onClick={() => emitter.emit("open-platform-terms")}
            >
              {t("terms")}
            </Typography>
          </Box>
        )}
      </Box>

      <ProfileDrawer
        open={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        defaultTab={profileDrawerTab}
      />
    </>
  );
};
export default BottomNavMenu;
